import { useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useDateSort } from '../../../../hooks/useDateSort';

/** 웰컴 리텐션 코호트 한 행 (date, cohortSize(N), retention: {day0, day1, ...} 원본 인원 수) */
export type FirstVisitRetentionRow = {
  date: string;
  cohortSize: number;
  retention: Record<string, number>;
};

const DATA_COLUMN_WIDTH = 72;

/** 비율(%) 구간별 배경색 (0~10% 연한 파랑 → 91~100% 진한 파랑) */
const RATE_BG_COLORS: { max: number; color: string }[] = [
  { max: 10, color: '#F3F7FF' },
  { max: 20, color: '#E7EFFF' },
  { max: 30, color: '#D8E4FF' },
  { max: 40, color: '#C7D7FA' },
  { max: 50, color: '#B3C8F8' },
  { max: 60, color: '#9FBAF7' },
  { max: 70, color: '#8BABF5' },
  { max: 80, color: '#769DF3' },
  { max: 90, color: '#628FF1' },
  { max: 100, color: '#4E80F0' },
];

function getRateBackgroundColor(rate: number): string {
  const r = Math.min(100, Math.max(0, rate));
  const found = RATE_BG_COLORS.find(({ max }) => r <= max);
  return found?.color ?? RATE_BG_COLORS[RATE_BG_COLORS.length - 1].color;
}

type Props = {
  rows: FirstVisitRetentionRow[];
};

export default function RouletteContentFirstVisitRetentionTable({ rows }: Props) {
  const { sorted, sortDirection, toggleSort } = useDateSort(rows, (r) => r.date, 'desc');

  const dayColumns = useMemo(() => {
    const days = new Set<number>();
    rows.forEach((row) => {
      Object.keys(row.retention).forEach((key) => {
        const match = /^day(\d+)$/.exec(key);
        if (match) days.add(Number(match[1]));
      });
    });
    return Array.from(days).sort((a, b) => a - b);
  }, [rows]);

  const formatNumber = (value: number) => Math.round(value).toLocaleString('ko-KR');

  return (
    <Box>
      <TableContainer
        sx={{
          backgroundColor: '#fff',
          borderRadius: 3,
          overflowX: 'auto',
          width: '100%',
          p: 2,
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  whiteSpace: 'nowrap',
                  backgroundColor: '#fafafa',
                  borderBottom: '1px solid #e0e0e0',
                  py: 0.75,
                  px: 1,
                  fontSize: '0.8rem',
                  width: 96,
                }}
              >
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  기준일
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  whiteSpace: 'nowrap',
                  backgroundColor: '#fafafa',
                  borderBottom: '1px solid #e0e0e0',
                  py: 0.75,
                  px: 1,
                  fontSize: '0.8rem',
                  width: DATA_COLUMN_WIDTH,
                  minWidth: DATA_COLUMN_WIDTH,
                }}
              >
                코호트(N)
              </TableCell>
              {dayColumns.map((d) => (
                <TableCell
                  key={d}
                  align="center"
                  sx={{
                    whiteSpace: 'nowrap',
                    backgroundColor: '#fafafa',
                    borderBottom: '1px solid #e0e0e0',
                    py: 0.75,
                    px: 1,
                    fontSize: '0.8rem',
                    width: DATA_COLUMN_WIDTH,
                    minWidth: DATA_COLUMN_WIDTH,
                  }}
                >
                  D{d}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.date}>
                <TableCell
                  align="center"
                  sx={{
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #e0e0e0',
                    py: 0.75,
                    px: 1,
                    fontSize: '0.8rem',
                    width: 96,
                  }}
                >
                  {row.date}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #e0e0e0',
                    py: 0.75,
                    px: 1,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    width: DATA_COLUMN_WIDTH,
                    minWidth: DATA_COLUMN_WIDTH,
                  }}
                >
                  {formatNumber(row.cohortSize)}
                </TableCell>
                {dayColumns.map((d) => {
                  const count = row.retention[`day${d}`];
                  const isUnavailable = count === undefined;
                  const rate =
                    !isUnavailable && row.cohortSize > 0
                      ? Math.round((count / row.cohortSize) * 1000) / 10
                      : 0;
                  return (
                    <TableCell
                      key={d}
                      align="center"
                      sx={{
                        whiteSpace: 'nowrap',
                        backgroundColor: isUnavailable ? '#ffffff' : getRateBackgroundColor(rate),
                        borderBottom: '1px solid #e0e0e0',
                        py: 0.75,
                        px: 1,
                        fontSize: '0.8rem',
                        width: DATA_COLUMN_WIDTH,
                        minWidth: DATA_COLUMN_WIDTH,
                      }}
                    >
                      {isUnavailable ? (
                        <Box component="span" sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                          –
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.2,
                          }}
                        >
                          <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
                            {rate}%
                          </Box>
                          <Box component="span" sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                            {formatNumber(count)}
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
